import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
var _templateObject;
import { css } from "@emotion/core";
import { border, lodestarColors, motion, space, ursaColors } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { themed } from "@smartsheet/sds-theme-internal";
import { toRgba } from "../../../constants/colors";
import { gridSize } from "../../../constants/grid";
import { sizes } from "../../../constants/spacing";
import { createFocusRingStyles } from "../../../util/createFocusRingStyles";
export var toggleTheme = themed({
  borderColor: {
    checked: "transparent",
    checkedHover: "transparent",
    checkedPressed: "transparent",
    checkedDisabled: "transparent",
    invalid: {
      "lodestar-light": "transparent",
      "ursa-light": ursaColors.red500
    },
    unchecked: {
      "lodestar-light": "transparent",
      "ursa-light": ursaColors.neutral200
    },
    uncheckedHover: {
      "lodestar-light": "transparent",
      "ursa-light": ursaColors.neutral200
    },
    uncheckedPressed: {
      "lodestar-light": "transparent",
      "ursa-light": ursaColors.neutral200
    },
    uncheckedDisabled: {
      "lodestar-light": "transparent",
      "ursa-light": ursaColors.neutral100
    }
  },
  backgroundColor: {
    checked: {
      "lodestar-light": lodestarColors.buttonPrimary,
      oldestar: "#0073ec",
      "ursa-light": ursaColors.blue500
    },
    checkedHover: {
      "lodestar-light": lodestarColors.focus,
      oldestar: "#005cbc",
      "ursa-light": ursaColors.blue600
    },
    checkedPressed: {
      "lodestar-light": lodestarColors.neutral,
      oldestar: "#005cbc",
      "ursa-light": ursaColors.blue700
    },
    checkedDisabled: {
      "lodestar-light": toRgba(lodestarColors.buttonPrimary, 0.4),
      oldestar: toRgba("#0073ec", 0.3),
      "ursa-light": ursaColors.blue200
    },
    invalid: {
      "lodestar-light": lodestarColors.buttonDestructive,
      "ursa-light": color("surface--work--background")
    },
    unchecked: {
      "lodestar-light": lodestarColors.neutralAccessible,
      "ursa-light": color("surface--work--background")
    },
    uncheckedHover: {
      "lodestar-light": lodestarColors.neutral,
      oldestar: "#005cbc",
      "ursa-light": color("neutral--background-alt")
    },
    uncheckedPressed: {
      "lodestar-light": lodestarColors.focus,
      oldestar: "#005cbc",
      "ursa-light": color("neutral--background-alt--hover")
    },
    uncheckedDisabled: {
      "lodestar-light": toRgba(lodestarColors.neutralAccessible, 0.4),
      "ursa-light": color("surface--work--background")
    }
  },
  focusRingComponentBorderWidth: {
    "lodestar-light": 4,
    "ursa-light": 3
  },
  focusRingComponentRadius: {
    medium: {
      "lodestar-light": 12,
      "ursa-light": 12
    },
    large: {
      "lodestar-light": 18,
      "ursa-light": 12
    }
  },
  handleColor: {
    checked: {
      "lodestar-light": lodestarColors.neutralLight40,
      "ursa-light": color("surface--work--background")
    },
    invalid: {
      "lodestar-light": lodestarColors.neutralLight40,
      "ursa-light": color("neutral--base")
    },
    unchecked: {
      "lodestar-light": lodestarColors.neutralLight40,
      "ursa-light": color("neutral--base")
    },
    uncheckedDisabled: {
      "lodestar-light": lodestarColors.neutralLight40,
      "ursa-light": color("neutral--background-alt--hover")
    }
  },
  handleMargin: {
    medium: {
      "lodestar-light": 1,
      "ursa-light": 2
    },
    large: 2
  },
  handleSize: {
    medium: {
      "lodestar-light": gridSize * 4 - sizes.xxSmall - sizes.xxSmall / 2,
      "ursa-light": 10
    },
    large: {
      "lodestar-light": gridSize * 6 - sizes.xSmall,
      "ursa-light": 12
    }
  },
  handleTranslateX: {
    medium: {
      offNoHover: "initial",
      offHover: "initial",
      onHover: {
        "lodestar-light": "translateX(14px)",
        "ursa-light": "translateX(12px)"
      },
      onNoHover: {
        "lodestar-light": "translateX(16px)",
        "ursa-light": "translateX(12px)"
      }
    },
    large: {
      offNoHover: "initial",
      offHover: {
        "lodestar-light": "translateX(4px)",
        "ursa-light": "initial"
      },
      onHover: {
        "lodestar-light": "translateX(20px)",
        "ursa-light": "translateX(14px)"
      },
      onNoHover: {
        "lodestar-light": "translateX(24px)",
        "ursa-light": "translateX(14px)"
      }
    }
  },
  height: {
    medium: {
      "lodestar-light": "".concat(gridSize * 4, "px"),
      "ursa-light": space.space40
    },
    large: {
      "lodestar-light": "".concat(gridSize * 6, "px"),
      "ursa-light": "calc(".concat(space.space40, " + ").concat(space.space10, ")")
    }
  },
  labelGap: {
    "lodestar-light": "12px",
    "ursa-light": "6px"
  },
  labelMarginTop: {
    medium: "-1px",
    large: {
      "lodestar-light": "3px",
      "ursa-light": "2px"
    }
  },
  motion: {
    animation: {
      "lodestar-light": "0.1s ease-in",
      "ursa-light": "".concat(motion.animation.default)
    },
    default: {
      "lodestar-light": "0.2s",
      "ursa-light": "all ".concat(motion.animation.default)
    }
  },
  width: {
    medium: {
      "lodestar-light": "".concat(gridSize * 8, "px"),
      "ursa-light": "30px"
    },
    large: {
      "lodestar-light": "".concat(gridSize * 12, "px"),
      "ursa-light": "34px"
    }
  }
});
export var getBackgroundColor = function getBackgroundColor(_ref) {
  var isActive = _ref.isActive,
    isChecked = _ref.isChecked,
    isDisabled = _ref.isDisabled,
    isInvalid = _ref.isInvalid,
    themeMode = _ref.themeMode;
  var backgroundColor = "";
  if (!isActive) {
    if (isChecked) {
      backgroundColor = toggleTheme.backgroundColor.checked(themeMode);
    } else {
      backgroundColor = toggleTheme.backgroundColor.unchecked(themeMode);
    }
  } else {
    if (isChecked) {
      backgroundColor = toggleTheme.backgroundColor.checkedPressed(themeMode);
    } else {
      backgroundColor = toggleTheme.backgroundColor.uncheckedPressed(themeMode);
    }
  }
  if (isDisabled) {
    if (isChecked) {
      backgroundColor = toggleTheme.backgroundColor.checkedDisabled(themeMode);
    } else {
      backgroundColor = toggleTheme.backgroundColor.uncheckedDisabled(themeMode);
    }
  }
  if (isInvalid) {
    backgroundColor = toggleTheme.backgroundColor.invalid(themeMode);
  }
  return backgroundColor;
};
export var getBorder = function getBorder(_ref2) {
  var isActive = _ref2.isActive,
    isChecked = _ref2.isChecked,
    isDisabled = _ref2.isDisabled,
    isInvalid = _ref2.isInvalid,
    themeMode = _ref2.themeMode;
  var borderColor = "";
  if (!isActive) {
    if (isChecked) {
      borderColor = toggleTheme.borderColor.checked(themeMode);
    } else {
      borderColor = toggleTheme.borderColor.unchecked(themeMode);
    }
  } else {
    if (isChecked) {
      borderColor = toggleTheme.borderColor.checkedPressed(themeMode);
    } else {
      borderColor = toggleTheme.borderColor.uncheckedPressed(themeMode);
    }
  }
  if (isDisabled) {
    if (isChecked) {
      borderColor = toggleTheme.borderColor.checkedDisabled(themeMode);
    } else {
      borderColor = toggleTheme.borderColor.uncheckedDisabled(themeMode);
    }
  }
  if (isInvalid) {
    borderColor = toggleTheme.borderColor.invalid(themeMode);
  }
  return "".concat(border.width20, " solid ").concat(borderColor);
};
export var getHandleColor = function getHandleColor(_ref3) {
  var isChecked = _ref3.isChecked,
    isDisabled = _ref3.isDisabled,
    isInvalid = _ref3.isInvalid,
    themeMode = _ref3.themeMode;
  var handleColor = "";
  if (isChecked) {
    handleColor = toggleTheme.handleColor.checked(themeMode);
  } else {
    if (isDisabled) {
      handleColor = toggleTheme.handleColor.uncheckedDisabled(themeMode);
    } else {
      handleColor = toggleTheme.handleColor.unchecked(themeMode);
    }
  }
  if (isInvalid) {
    handleColor = toggleTheme.handleColor.invalid(themeMode);
  }
  return handleColor;
};
export var getSlideHoverStyles = function getSlideHoverStyles(_ref4) {
  var isActive = _ref4.isActive,
    isChecked = _ref4.isChecked,
    isDisabled = _ref4.isDisabled,
    isInvalid = _ref4.isInvalid,
    themeMode = _ref4.themeMode;
  var backgroundColor;
  if (!isDisabled && !isActive) {
    // Disabled and currently active toggles have no hover change.
    backgroundColor = isInvalid ? toggleTheme.backgroundColor.invalid(themeMode) : isChecked ? toggleTheme.backgroundColor.checkedHover(themeMode) : toggleTheme.backgroundColor.uncheckedHover(themeMode);
  }
  return css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n        &:hover {\n            ", ";\n        }\n    "])), backgroundColor ? "background-color: ".concat(backgroundColor, ";") : "");
};
export var getFocusRingStyles = function getFocusRingStyles(_ref5) {
  var size = _ref5.size,
    themeMode = _ref5.themeMode;
  return createFocusRingStyles({
    theme: themeMode,
    gap: 0,
    componentBorderWidth: toggleTheme.focusRingComponentBorderWidth(themeMode),
    componentRadius: toggleTheme.focusRingComponentRadius[size](themeMode),
    ringWidth: 2,
    focusSelector: ":focus-within"
  });
};
//# sourceMappingURL=toggleTheme.js.map